import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import FullCalendar from "@fullcalendar/react";
import timeGridPlugin from "@fullcalendar/timegrid";
import axios from "axios";
import moment from "moment";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { AiFillCaretLeft, AiFillCaretRight } from "react-icons/ai";
import { serverUrl } from "../../../../../nestserver";

const DailyScheduleTask = ({
  filterDay,
  filterClassId,
  filterEventId,
  teacherId,
  role,
  filterInstructorId,
}) => {
  const [currentDate, setCurrentDate] = useState(moment().format("YYYY-MM-DD"));
  const calendarRef = useRef(null);
  const [timeTable, setTimeTable] = useState([]);
  const [events, setEvents] = useState([]);
  const debounceTimeout = useRef(null);
  const [holidays, setHolidays] = useState([]);

  const getEventsForDate = useCallback(
    (date, fetchedHolidays) => {
      const events = [];

      // Add holiday data
      if (Array.isArray(fetchedHolidays)) {
        for (const item of fetchedHolidays) {
          const eventObj = {
            title: item?.name,
            start: `${moment(item?.from).format("YYYY-MM-DD")}T00:00:00`,
            end: `${moment(item?.to).add(1, "days").format("YYYY-MM-DD")}T00:00:00`,
            extendedProps: {
              isHoliday: true,
              type: item?.type,
              category: item?.category,
              isDefault: item?.isDefault,
              colorCode: item?.isDefault ? "#FFB300" : "#F50057",
              textColor: item?.isDefault ? "#805B00" : "#FFFFFF",
            },
            allDay: true,
            display: "background",
            backgroundColor: item?.isDefault
              ? "rgba(255, 179, 0, 0.45)"
              : "rgba(245, 0, 87, 0.65)",
          };
          events.push(eventObj);
        }
      }

      // Add timetable data
      if (Array.isArray(timeTable?.result?.data)) {
        for (const item of timeTable?.result?.data) {
          if (Array.isArray(item?.timetable)) {
            if (filterDay === "weekly") {
              for (let i = 0; i < 7; i++) {
                const currentDay = moment(date).startOf("week").add(i, "days");

                const filteredData = item?.timetable.filter(
                  (fItem) =>
                    fItem.day === currentDay.format("ddd").toLowerCase(),
                );

                for (const scheduleData of filteredData) {
                  const startTime = `${scheduleData?.fromTime?.hours}:${scheduleData?.fromTime?.minutes}`;
                  const endTime = `${scheduleData?.endTime?.hours}:${scheduleData?.endTime?.minutes}`;
                  const event = {
                    title: scheduleData?.name,
                    start: `${currentDay.format("YYYY-MM-DD")}T${startTime}`,
                    end: `${currentDay.format("YYYY-MM-DD")}T${endTime}`,
                    backgroundColor: "#2979FF",
                    borderColor: "#2979FF",
                    textColor: "#FFFFFF",
                    extendedProps: {
                      subjectName: scheduleData?.subject?.name,
                      teacherName: `${scheduleData?.subject?.teacher?.teacher?.first_name} ${scheduleData?.subject?.teacher?.teacher?.last_name}`,
                      timeRange: `${startTime}-${endTime}`,
                      class: item?.class?.name,
                      isTimetable: true,
                    },
                    allDay: false,
                  };
                  events.push(event);
                }
              }
            } else if (filterDay === "day") {
              const filteredData = item?.timetable.filter(
                (fItem) =>
                  fItem.day === moment(date).format("ddd").toLowerCase(),
              );
              for (const scheduleData of filteredData) {
                const startTime = `${scheduleData?.fromTime?.hours}:${scheduleData?.fromTime?.minutes}`;
                const endTime = `${scheduleData?.endTime?.hours}:${scheduleData?.endTime?.minutes}`;
                const event = {
                  title: scheduleData?.name,
                  start: `${date}T${startTime}`,
                  end: `${date}T${endTime}`,
                  backgroundColor: "#2979FF",
                  borderColor: "#2979FF",
                  textColor: "#FFFFFF",
                  extendedProps: {
                    subjectName: scheduleData?.subject?.name,
                    teacherName: `${scheduleData?.subject?.teacher?.teacher?.first_name} ${scheduleData?.subject?.teacher?.teacher?.last_name}`,
                    timeRange: `${startTime}-${endTime}`,
                    class: item?.class?.name,
                    isTimetable: true,
                  },
                  allDay: false,
                };
                events.push(event);
              }
            }
          }
        }
      }
      // Add subevents data
      if (Array.isArray(timeTable?.result?.subevents)) {
        for (const item of timeTable?.result?.subevents) {
          const { event, event_date } = item;
          const eventStartDate = event_date;
          const eventObj = {
            title: event?.name,
            start: eventStartDate,
            extendedProps: {
              isSubevent: true,
              colorCode: event?.event_type?.color_code,
              dateRange: `${moment(eventStartDate)}`,
            },
            allDay: true,
          };
          events.push(eventObj);
        }
      }
      return events;
    },
    [timeTable, filterDay],
  );
  const handleDateChange = useCallback(
    async (info) => {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }

      debounceTimeout.current = setTimeout(async () => {
        try {
          let startDate, endDate;
          let viewYear;

          if (filterDay === "day") {
            startDate = moment(currentDate).format("YYYY-MM-DD");
            endDate = moment(currentDate).format("YYYY-MM-DD");
            viewYear = moment(currentDate).year();
          } else if (filterDay === "weekly") {
            const startOfWeek = moment(currentDate).startOf("week");
            const endOfWeek = moment(currentDate).endOf("week");

            startDate = startOfWeek.format("YYYY-MM-DD");
            endDate = endOfWeek.format("YYYY-MM-DD");
            viewYear = startOfWeek.year();
          } else {
            startDate = moment(info.startStr)
              .startOf("month")
              .format("YYYY-MM-DD");
            endDate = moment(info.endStr).endOf("month").format("YYYY-MM-DD");
            viewYear = moment(info.startStr).year();
          }

          // Fetch Timetable
          const res = await axios({
            method: "get",
            url: serverUrl + "/event/schedule",
            params: {
              class: filterClassId,
              startDate,
              endDate,
              instructor:
                filterInstructorId ||
                (role === "instructor" && teacherId ? teacherId : null),
              event: filterEventId,
            },
            headers: {
              Authorization: `Bearer ${window.localStorage.getItem(
                "jwt-token",
              )}`,
            },
          });
          setTimeTable(res.data);

          // Fetch Holidays for the current view year
          const holidayRes = await axios({
            method: "get",
            url: serverUrl + "/holiday",
            params: { year: viewYear },
            headers: {
              Authorization: `Bearer ${window.localStorage.getItem(
                "jwt-token",
              )}`,
            },
          });
          setHolidays(holidayRes.data);
        } catch (error) {
          console.error("Error fetching data:", error);
        }
      }, 500);
    },
    [
      filterDay,
      filterClassId,
      filterInstructorId,
      filterEventId,
      currentDate,
      role,
      teacherId,
    ],
  );

  useEffect(() => {
    if (calendarRef.current) {
      const calendarApi = calendarRef.current.getApi();

      if (filterDay === "weekly") {
        const startOfWeek = moment(currentDate).startOf("week");
        calendarApi.gotoDate(startOfWeek.format("YYYY-MM-DD"));
        calendarApi.changeView("timeGridWeek");
      } else if (filterDay === "day") {
        calendarApi.gotoDate(currentDate);
        calendarApi.changeView("timeGridDay");
      } else {
        calendarApi.changeView("dayGridMonth");
      }

      const handleDatesSet = (info) => {
        handleDateChange(info);
      };

      calendarApi.on("datesSet", handleDatesSet);

      return () => {
        calendarApi.off("datesSet", handleDatesSet);
      };
    }
  }, [filterDay, handleDateChange, currentDate]);

  const handlePrev = () => {
    if (calendarRef.current) {
      const calendarApi = calendarRef.current.getApi();
      let prevDate;

      if (filterDay === "day") {
        prevDate = moment(currentDate).subtract(1, "days").format("YYYY-MM-DD");
        setCurrentDate(prevDate);
      } else if (filterDay === "weekly") {
        prevDate = moment(currentDate)
          .subtract(1, "weeks")
          .startOf("week")
          .format("YYYY-MM-DD");
        setCurrentDate(prevDate);
      } else {
        prevDate = moment(currentDate)
          .subtract(1, "months")
          .startOf("month")
          .format("YYYY-MM-DD");
        setCurrentDate(prevDate);
      }

      calendarApi.gotoDate(prevDate);
      handleDateChange({ startStr: prevDate, endStr: prevDate });
    }
  };

  const handleNext = () => {
    if (calendarRef.current) {
      const calendarApi = calendarRef.current.getApi();
      let nextDate;

      if (filterDay === "day") {
        nextDate = moment(currentDate).add(1, "days").format("YYYY-MM-DD");
        setCurrentDate(nextDate);
      } else if (filterDay === "weekly") {
        nextDate = moment(currentDate)
          .add(1, "weeks")
          .startOf("week")
          .format("YYYY-MM-DD");
        setCurrentDate(nextDate);
      } else {
        nextDate = moment(currentDate)
          .add(1, "months")
          .startOf("month")
          .format("YYYY-MM-DD");
        setCurrentDate(nextDate);
      }

      calendarApi.gotoDate(nextDate);
      handleDateChange({ startStr: nextDate, endStr: nextDate });
    }
  };
  useEffect(() => {
    const fetchEvents = () => {
      // Re-evaluate events whenever timeTable, holidays, or currentDate changes
      const aggregatedEvents = getEventsForDate(currentDate, holidays);
      setEvents(aggregatedEvents);
    };
    fetchEvents();
  }, [timeTable, holidays, currentDate, filterDay, getEventsForDate]);

  return (
    <div className="pt-5 react-calendar">
      <div className="flex gap-4 pt-4">
        <button onClick={handlePrev} className="w-fit">
          <AiFillCaretLeft size={35} />
        </button>
        <FullCalendar
          ref={calendarRef}
          plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
          initialView={
            filterDay === "weekly"
              ? "timeGridWeek"
              : filterDay === "day"
                ? "timeGridDay"
                : "dayGridMonth"
          }
          headerToolbar={{
            start: "pre",
            center: "title",
            end: "start",
          }}
          events={events}
          eventContent={(arg) => {
            if (arg?.event?.extendedProps?.isHoliday) {
              const textColor = arg?.event?.extendedProps?.textColor || "#fff";
              const textShadow =
                textColor === "#FFFFFF"
                  ? "0px 1px 3px rgba(0,0,0,0.6)"
                  : "none";
              return (
                <div
                  style={{
                    textAlign: "center",
                    width: "100%",
                    padding: "6px",
                    fontWeight: "800",
                    fontSize: "1.1rem",
                    color: textColor,
                    textShadow: textShadow,
                    letterSpacing: "0.5px",
                  }}
                >
                  <span className="capitalize">{arg?.event?.title}</span>
                </div>
              );
            }
            return (
              <div>
                {arg?.event?.extendedProps?.isTimetable ? (
                  <div style={{ width: "500px" }}>
                    <div className="capitalize">
                      {arg?.event?.extendedProps?.subjectName}
                    </div>
                    <div className="capitalize">
                      {arg?.event?.extendedProps?.teacherName}
                    </div>
                    <div className="capitalize">
                      {arg?.event?.extendedProps?.class}
                    </div>
                    <div className="capitalize">
                      {""} {arg?.event?.extendedProps?.timeRange}
                    </div>
                  </div>
                ) : (
                  <>
                    <div
                      style={{
                        background: arg?.event?.extendedProps?.colorCode,
                        height: "10px",
                        width: "10px",
                        borderRadius: "50%",
                        display: "inline-block",
                        marginRight: "4px",
                        verticalAlign: "middle",
                      }}
                    ></div>{" "}
                    <span className="capitalize">{arg?.event?.title}</span>
                  </>
                )}
              </div>
            );
          }}
          // dayMaxEventRows={true}
          // views={{
          //   timeGrid: {
          //     dayMaxEventRows: 3, // adjust to 6 only for timeGridWeek/timeGridDay
          //   },
          // }}
          progressiveEventRendering={true}
          dayMaxEventRows={3}
          eventMaxStack={1}
          dayHeaderFormat={{ weekday: "long" }}
          contentHeight="auto"
          slotMaxTime={"18:00:00"}
          slotMinTime={"07:00:00"}
          slotDuration="00:15:00"
          slotLabelInterval="01:00"
          datesSet={handleDateChange}
        />
        <button onClick={handleNext} className="w-fit">
          <AiFillCaretRight size={35} />
        </button>
      </div>
    </div>
  );
};

export default DailyScheduleTask;
